import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, Input, Label, Message, Modal, Pagination, Popup, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, setPromptShown, shouldShowPrompt, showError, showInfo, showSuccess, timestamp2string } from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderGroup, renderNumber } from '../helpers/render';

function renderTimestamp(timestamp) {
  return (
    <>
      {timestamp2string(timestamp)}
    </>
  );
}

// 渠道类型已移除，且后端余额查询恒返回 not supported，故一律显示“不支持”
function renderBalance() {
  return <span>不支持</span>;
}

const ChannelsTable = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState(false);
  const [exportingAbilities, setExportingAbilities] = useState(false);
  // 导出时按用户分组过滤，空字符串表示全部分组
  const [exportGroup, setExportGroup] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [showPrompt, setShowPrompt] = useState(shouldShowPrompt("channel-test"));

  const fetchGroups = async () => {
    try {
      const res = await API.get('/api/group/');
      setGroupOptions([
        { key: '', text: '全部分组', value: '' },
        ...(res.data.data || []).map((g) => ({ key: g, text: g, value: g })),
      ]);
    } catch (e) {
      showError(e.message);
    }
  };

  const exportAbilities = async () => {
    setExportingAbilities(true);
    try {
      const res = await API.get('/api/channel/export_abilities', {
        responseType: 'blob',
        params: exportGroup ? { group: exportGroup } : {},
      });
      // 后端校验失败时返回 JSON，而不是 CSV
      if ((res.data?.type || '').includes('application/json')) {
        const { message } = JSON.parse(await res.data.text());
        showError('导出失败：' + (message || '未知错误'));
        return;
      }
      const disposition = res.headers['content-disposition'] || '';
      let filename = 'abilities.csv';
      const matchStar = disposition.match(/filename\*=UTF-8''([^\s;]+)/);
      if (matchStar) {
        filename = decodeURIComponent(matchStar[1]);
      } else {
        const match = disposition.match(/filename=([^\s;]+)/);
        if (match) filename = match[1];
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showError('导出失败：' + (e?.message || '未知错误'));
    } finally {
      setExportingAbilities(false);
    }
  };

  // 批量导入：先预览变更摘要，确认后再写库
  const importInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importPlan, setImportPlan] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [previewingImport, setPreviewingImport] = useState(false);
  const [applyingImport, setApplyingImport] = useState(false);

  const closeImportModal = () => {
    setImportFile(null);
    setImportPlan(null);
    setImportErrors([]);
  };

  const onImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    // 清空 value，同一个文件改完后可以再次选择
    e.target.value = '';
    if (!file) return;
    setPreviewingImport(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await API.post('/api/channel/import_abilities/preview', form);
      if (!res) return;
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setImportFile(file);
      setImportErrors(data.errors || []);
      setImportPlan(data.errors ? null : data);
    } finally {
      setPreviewingImport(false);
    }
  };

  const applyImport = async () => {
    if (!importFile || !importPlan) return;
    setApplyingImport(true);
    try {
      const form = new FormData();
      form.append('file', importFile);
      form.append('digest', importPlan.digest);
      const res = await API.post('/api/channel/import_abilities', form);
      if (!res) return;
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess(`导入完成：新增 ${data.inserted}，更新 ${data.updated}，未变化 ${data.unchanged}`);
      closeImportModal();
      await refresh();
    } finally {
      setApplyingImport(false);
    }
  };

  const importHasChanges = importPlan && (importPlan.inserts.length > 0 || importPlan.updates.length > 0);

  const loadChannels = async (startIdx) => {
    const res = await API.get(`/api/channel/?p=${startIdx}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setChannels(data);
      } else {
        let newChannels = [...channels];
        newChannels.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setChannels(newChannels);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(channels.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadChannels(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    await loadChannels(activePage - 1);
  };

  useEffect(() => {
    loadChannels(0)
      .then()
      .catch((reason) => {
        showError(reason);
      });
    fetchGroups();
  }, []);

  const manageChannel = async (id, action, idx, value) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/channel/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/channel/', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/channel/', data);
        break;
      case 'priority':
        if (value === '') {
          return;
        }
        data.priority = parseInt(value);
        res = await API.put('/api/channel/', data);
        break;
      case 'weight':
        if (value === '') {
          return;
        }
        data.weight = parseInt(value);
        if (data.weight < 0) {
          data.weight = 0;
        }
        res = await API.put('/api/channel/', data);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess('操作成功完成！');
      let channel = res.data.data;
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      if (action === 'delete') {
        newChannels[realIdx].deleted = true;
      } else {
        newChannels[realIdx].status = channel.status;
      }
      setChannels(newChannels);
    } else {
      showError(message);
    }
  };

  const renderStatus = (status) => {
    switch (status) {
      case 1:
        return <Label basic color='green'>已启用</Label>;
      case 2:
        return (
          <Popup
            trigger={<Label color='red'>已禁用</Label>}
            content='本渠道被手动禁用'
            basic
          />
        );
      case 3:
        return (
          <Popup
            trigger={<Label color='orange'>自动禁用</Label>}
            content='本渠道被程序自动禁用'
            basic
          />
        );
      default:
        return (
          <Label basic color='grey'>
            未知状态
          </Label>
        );
    }
  };

  const renderResponseTime = (responseTime) => {
    let time = responseTime / 1000;
    time = time.toFixed(2) + ' 秒';
    if (responseTime === 0) {
      return <Label basic color='grey'>未测试</Label>;
    } else if (responseTime <= 1000) {
      return <Label basic color='green'>{time}</Label>;
    } else if (responseTime <= 3000) {
      return <Label basic color='olive'>{time}</Label>;
    } else if (responseTime <= 5000) {
      return <Label basic color='yellow'>{time}</Label>;
    } else {
      return <Label basic color='red'>{time}</Label>;
    }
  };

  const searchChannels = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadChannels(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/channel/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setChannels(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const testChannel = async (id, name, idx) => {
    const res = await API.get(`/api/channel/test/${id}/`);
    const { success, message, time } = res.data;
    if (success) {
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      newChannels[realIdx].response_time = time * 1000;
      newChannels[realIdx].test_time = Date.now() / 1000;
      setChannels(newChannels);
      showInfo(`通道 ${name} 测试成功，耗时 ${time.toFixed(2)} 秒。`);
    } else {
      showError(message);
    }
  };

  const testAllChannels = async () => {
    const res = await API.get(`/api/channel/test`);
    const { success, message } = res.data;
    if (success) {
      showInfo('已成功开始测试所有通道，请刷新页面查看结果。');
    } else {
      showError(message);
    }
  };

  const deleteAllDisabledChannels = async () => {
    const res = await API.delete(`/api/channel/disabled`);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(`已删除所有禁用渠道，共计 ${data} 个`);
      await refresh();
    } else {
      showError(message);
    }
  };

  const updateChannelBalance = async (id, name, idx) => {
    const res = await API.get(`/api/channel/update_balance/${id}/`);
    const { success, message, balance } = res.data;
    if (success) {
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      newChannels[realIdx].balance = balance;
      newChannels[realIdx].balance_updated_time = Date.now() / 1000;
      setChannels(newChannels);
      showInfo(`通道 ${name} 余额更新成功！`);
    } else {
      showError(message);
    }
  };

  const updateAllChannelsBalance = async () => {
    setUpdatingBalance(true);
    const res = await API.get(`/api/channel/update_balance`);
    const { success, message } = res.data;
    if (success) {
      showInfo('已更新完毕所有已启用通道余额！');
    } else {
      showError(message);
    }
    setUpdatingBalance(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortChannel = (key) => {
    if (channels.length === 0) return;
    setLoading(true);
    let sortedChannels = [...channels];
    sortedChannels.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedChannels[0].id === channels[0].id) {
      sortedChannels.reverse();
    }
    setChannels(sortedChannels);
    setLoading(false);
  };


  return (
    <>
      <Form onSubmit={searchChannels}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder='搜索渠道的 ID，名称和密钥 ...'
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>
      {
        showPrompt && (
          <Message onDismiss={() => {
            setShowPrompt(false);
            setPromptShown("channel-test");
          }}>
            当前版本测试是通过按照 OpenAI API 格式使用 gpt-3.5-turbo
            模型进行非流式请求实现的，因此测试报错并不一定代表通道不可用，该功能后续会修复。

            另外，OpenAI 渠道已经不再支持通过 key 获取余额，因此余额显示为 0。对于支持的渠道类型，请点击余额进行刷新。
          </Message>
        )
      }
      <Table basic compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('id');
              }}
            >
              ID
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('name');
              }}
            >
              名称
            </Table.HeaderCell>
            {/* <Table.HeaderCell */}
            {/*   style={{ cursor: 'pointer' }} */}
            {/*   onClick={() => { */}
            {/*     sortChannel('group'); */}
            {/*   }} */}
            {/* > */}
            {/*   分组 */}
            {/* </Table.HeaderCell> */}
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('status');
              }}
            >
              状态
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('response_time');
              }}
            >
              响应时间
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('balance');
              }}
            >
              余额
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('priority');
              }}
            >
              优先级
            </Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {channels
            .slice(
              (activePage - 1) * ITEMS_PER_PAGE,
              activePage * ITEMS_PER_PAGE
            )
            .map((channel, idx) => {
              if (channel.deleted) return <></>;
              const isDisabled = channel.status !== 1;
              return (
                <Table.Row key={channel.id} style={isDisabled ? { opacity: 0.45, background: 'rgba(0,0,0,0.03)' } : undefined}>
                  <Table.Cell>{channel.id}</Table.Cell>
                  <Table.Cell>{channel.name ? channel.name : '无'}</Table.Cell>
                  {/* <Table.Cell>{renderGroup(channel.group)}</Table.Cell> */}
                  <Table.Cell>{renderStatus(channel.status)}</Table.Cell>
                  <Table.Cell>
                    <Popup
                      content={channel.test_time ? renderTimestamp(channel.test_time) : '未测试'}
                      key={channel.id}
                      trigger={renderResponseTime(channel.response_time)}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Popup
                      trigger={<span onClick={() => {
                        updateChannelBalance(channel.id, channel.name, idx);
                      }} style={{ cursor: 'pointer' }}>
                      {renderBalance()}
                    </span>}
                      content='点击更新'
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Popup
                      trigger={<Input type='number' defaultValue={channel.priority} onBlur={(event) => {
                        manageChannel(
                          channel.id,
                          'priority',
                          idx,
                          event.target.value
                        );
                      }}>
                        <input style={{ maxWidth: '60px' }} />
                      </Input>}
                      content='渠道选择优先级，越高越优先'
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div>
                      <Button
                        size={'small'}
                        positive
                        onClick={() => {
                          testChannel(channel.id, channel.name, idx);
                        }}
                      >
                        测试
                      </Button>
                      {/*<Button*/}
                      {/*  size={'small'}*/}
                      {/*  positive*/}
                      {/*  loading={updatingBalance}*/}
                      {/*  onClick={() => {*/}
                      {/*    updateChannelBalance(channel.id, channel.name, idx);*/}
                      {/*  }}*/}
                      {/*>*/}
                      {/*  更新余额*/}
                      {/*</Button>*/}
                      <Button
                        size={'small'}
                        onClick={() => {
                          manageChannel(
                            channel.id,
                            channel.status === 1 ? 'disable' : 'enable',
                            idx
                          );
                        }}
                      >
                        {channel.status === 1 ? '禁用' : '启用'}
                      </Button>
                      <Button
                        size={'small'}
                        as={Link}
                        to={'/channel/' + channel.id + '/abilities?name=' + encodeURIComponent(channel.name)}
                      >
                        能力
                      </Button>
                      <Button
                        size={'small'}
                        as={Link}
                        to={'/channel/edit/' + channel.id}
                      >
                        编辑
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='9'>
              <Button size='small' as={Link} to='/channel/add' loading={loading}>
                添加新的渠道
              </Button>
              {/* <Button size='small' loading={loading} onClick={testAllChannels}> */}
              {/*   测试所有渠道 */}
              {/* </Button> */}
              {/* <Button size='small' onClick={updateAllChannelsBalance} */}
              {/*         loading={loading || updatingBalance}>更新已启用渠道余额</Button> */}
              {/* <Popup */}
              {/*   trigger={ */}
              {/*     <Button size='small' loading={loading}> */}
              {/*       删除禁用渠道 */}
              {/*     </Button> */}
              {/*   } */}
              {/*   on='click' */}
              {/*   flowing */}
              {/*   hoverable */}
              {/* > */}
              {/*   <Button size='small' loading={loading} negative onClick={deleteAllDisabledChannels}> */}
              {/*     确认删除 */}
              {/*   </Button> */}
              {/* </Popup> */}
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(channels.length / ITEMS_PER_PAGE) +
                  (channels.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
              <Button size='small' onClick={refresh} loading={loading}>刷新</Button>
              <Dropdown
                selection
                compact
                options={groupOptions}
                value={exportGroup}
                onChange={(e, { value }) => setExportGroup(value)}
                placeholder='全部分组'
                aria-label='导出模型的用户分组'
                style={{ marginRight: '0.25em' }}
              />
              <Button size='small' onClick={exportAbilities} loading={exportingAbilities}>
                {exportGroup ? `导出 ${exportGroup} 分组模型` : '导出所有模型'}
              </Button>
              <Button
                size='small'
                onClick={() => importInputRef.current && importInputRef.current.click()}
                loading={previewingImport}
                disabled={previewingImport}
              >
                导入模型
              </Button>
              <input
                ref={importInputRef}
                type='file'
                accept='.csv,text/csv'
                style={{ display: 'none' }}
                onChange={onImportFileChange}
                aria-label='选择要导入的模型 CSV 文件'
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={!!importFile} onClose={closeImportModal} size='large'>
        <Modal.Header>导入模型{importFile ? `：${importFile.name}` : ''}</Modal.Header>
        <Modal.Content scrolling>
          {importErrors.length > 0 && (
            <Message negative>
              <Message.Header>文件有 {importErrors.length} 处错误，请修改后重新导入（不会写入任何数据）</Message.Header>
              <Message.List>
                {importErrors.map((e, i) => (
                  <Message.Item key={i}>{e.line ? `第 ${e.line} 行：` : ''}{e.message}</Message.Item>
                ))}
              </Message.List>            </Message>
          )}
          {importPlan && (
            <>
              <Message info>
                新增 <b>{importPlan.inserts.length}</b> / 更新 <b>{importPlan.updates.length}</b> / 未变化{' '}
                <b>{importPlan.unchanged}</b>。CSV 中没有的模型不会被删除。
              </Message>
              {importPlan.inserts.length > 0 && (
                <>
                  <h4>新增</h4>
                  <Table compact celled size='small'>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>行</Table.HeaderCell>
                        <Table.HeaderCell>渠道</Table.HeaderCell>
                        <Table.HeaderCell>分组</Table.HeaderCell>
                        <Table.HeaderCell>模型</Table.HeaderCell>
                        <Table.HeaderCell>启用</Table.HeaderCell>
                        <Table.HeaderCell>输入倍率</Table.HeaderCell>
                        <Table.HeaderCell>输出倍率</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {importPlan.inserts.map((ins) => (
                        <Table.Row key={ins.line}>
                          <Table.Cell>{ins.line}</Table.Cell>
                          <Table.Cell>{ins.ability.channel_id} {ins.channel_name}</Table.Cell>
                          <Table.Cell>{ins.ability.group}</Table.Cell>
                          <Table.Cell>{ins.ability.model}</Table.Cell>
                          <Table.Cell>{ins.ability.enabled ? '是' : '否'}</Table.Cell>
                          <Table.Cell>{ins.ability.model_ratio}</Table.Cell>
                          <Table.Cell>{ins.ability.completion_ratio}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </>
              )}
              {importPlan.updates.length > 0 && (
                <>
                  <h4>更新</h4>
                  <Table compact celled size='small'>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>行</Table.HeaderCell>
                        <Table.HeaderCell>渠道</Table.HeaderCell>
                        <Table.HeaderCell>分组</Table.HeaderCell>
                        <Table.HeaderCell>模型</Table.HeaderCell>
                        <Table.HeaderCell>变更</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {importPlan.updates.map((up) => (
                        <Table.Row key={up.line}>
                          <Table.Cell>{up.line}</Table.Cell>
                          <Table.Cell>{up.channel_id}</Table.Cell>
                          <Table.Cell>{up.group}</Table.Cell>
                          <Table.Cell>{up.model}</Table.Cell>
                          <Table.Cell>
                            {up.changes.map((ch) => (
                              <div key={ch.field}>
                                {ch.field}：<code>{ch.old === '' ? '(空)' : ch.old}</code> →{' '}
                                <code>{ch.new === '' ? '(空)' : ch.new}</code>
                              </div>
                            ))}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeImportModal} disabled={applyingImport}>取消</Button>
          <Button
            positive
            onClick={applyImport}
            loading={applyingImport}
            disabled={!importHasChanges || importErrors.length > 0 || applyingImport}
          >
            确认导入
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ChannelsTable;
